import { useCallback } from "react";
import { EMPTY_CHAT_STATE, useTestingStore } from "@/stores/testingStore";

// Re-exported so existing imports (`@/hooks/testing/useTestingChat`) keep working.
export type { RenderedMessage, TestingChatActivity } from "@/stores/testingStore";

/**
 * Thin selector over the module-level `testingStore` for a given chat scope. All state and the live
 * SSE stream live in the store (which survives tab navigation natively); this hook just exposes the
 * current scope's slice + scope-bound actions with the shape the Testing UI already expects.
 */
export function useTestingChat(scopeKey = "new") {
	const current = useTestingStore((s) => s.byScope[scopeKey]) ?? EMPTY_CHAT_STATE;
	// Streaming is a single global stream, but only ONE scope owns it — bind the
	// flag to THIS scope so a different chat never renders a foreign stream's
	// spinner/stop button (moveScope retargets _streamScope on promotion).
	const streaming = useTestingStore((s) => s.streaming && s._streamScope === scopeKey);
	const error = useTestingStore((s) => s.error);
	const streamEndTick = useTestingStore((s) => s.streamEndTick);

	const sendAction = useTestingStore((s) => s.send);
	const cancel = useTestingStore((s) => s.cancel);
	const resetScope = useTestingStore((s) => s.resetScope);
	const moveScope = useTestingStore((s) => s.moveScope);
	const postRepairPromptAction = useTestingStore((s) => s.postRepairPrompt);

	const send = useCallback(
		(text: string, activePlanId?: string | null) => sendAction(scopeKey, text, activePlanId),
		[sendAction, scopeKey],
	);
	const reset = useCallback((key: string = scopeKey) => resetScope(key), [resetScope, scopeKey]);
	const postRepairPrompt = useCallback(
		(runId: string, text: string) => postRepairPromptAction(scopeKey, runId, text),
		[postRepairPromptAction, scopeKey],
	);

	return {
		messages: current.messages,
		streaming,
		error,
		lastProposedPlanId: current.lastProposedPlanId,
		phase: current.phase,
		activity: current.activity,
		streamEndTick,
		scopeKey,
		send,
		cancel,
		reset,
		moveScope,
		postRepairPrompt,
	};
}
